import React, { useState, useEffect } from 'react';
import styled, { useTheme, keyframes, css, createGlobalStyle, ThemeProvider } from 'styled-components';
import { Warning, CheckmarkAlt, InfoAlt, Down, Check, RemoveAlt, Spinner as Spinner$1 } from '@urbaninfrastructure/react-icons';
import { space as space$1, compose, layout, color, flexbox, typography, position, border, shadow, display, textShadow, system, fontSize, lineHeight, variant, grid, borderRadius, boxShadow, width, maxWidth as maxWidth$1, height, verticalAlign } from 'styled-system';
import { neutral, palette, paletteLight, paletteDark, accent, state, themeColors } from '@urbaninfrastructure/design-tokens';
import usePortal from 'react-useportal';
import { useReducedMotion, motion, AnimatePresence } from 'framer-motion';

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

function _taggedTemplateLiteralLoose(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }

  strings.raw = raw;
  return strings;
}

// @ts-ignore
var emToPx = function emToPx(em) {
  var value = em.split('em')[0];
  return parseFloat(value);
};
var REG = /^[mp][trblxy]?$/;
var rhythm = function rhythm(fn) {
  return function (props) {
    var spaceBoxesKeys = Object.keys(props.theme.rhythms || {});
    var keys = Object.keys(props) // @ts-ignore
    .filter(function (key) {
      return REG.test(key) && spaceBoxesKeys.includes(props[key]);
    }).sort();
    keys.forEach(function (key) {
      // @ts-ignore
      props[key] = props.theme.rhythms[props[key]];
    });
    return fn(props);
  };
};
function getTypoStyles(props) {
  var typoStyle = props.typoStyle && props.theme.typoStyles[props.typoStyle];
  var applyTypoStyle = !props.fontSize;

  if (props.lead) {
    typoStyle = props.theme.typoStyles.sm;
    delete typoStyle.fontWeight;
  }

  if (props.small) {
    typoStyle = props.theme.typoStyles.xxs;
  }

  return {
    fontSize: applyTypoStyle && typoStyle ? typoStyle.fontSize : props.fontSize,
    lineHeight: applyTypoStyle && typoStyle ? typoStyle.lineHeight : props.lineHeight,
    fontWeight: applyTypoStyle && typoStyle && typoStyle.fontWeight ? typoStyle.fontWeight : props.fontWeight
  };
} // useTheme is not typede yet
// https://github.com/DefinitelyTyped/DefinitelyTyped/issues/39450

function useStyledComponentsTheme() {
  return useTheme();
}
var printed = {};
function warnOnce(message) {
  if (printed[message]) {
    return;
  }

  printed[message] = true;

  if (typeof console !== 'undefined' && console.warn) {
    console.warn(message);
  }
}

function _templateObject() {
  var data = _taggedTemplateLiteralLoose(["\n  box-sizing: border-box;\n  ", ";\n  ", ";\n"]);

  _templateObject = function _templateObject() {
    return data;
  };

  return data;
}
var Box = /*#__PURE__*/styled.div( /*#__PURE__*/_templateObject(), /*#__PURE__*/rhythm(space$1), /*#__PURE__*/compose(layout, color, flexbox, typography, position, border, shadow));

function _templateObject$1() {
  var data = _taggedTemplateLiteralLoose(["\n  display: ", ";\n  ", ";\n"]);

  _templateObject$1 = function _templateObject() {
    return data;
  };

  return data;
}

var column = function column(props) {
  return props.column ? "flex-direction:column;" : null;
};

var Flex = /*#__PURE__*/styled(Box)( /*#__PURE__*/_templateObject$1(), function (_ref) {
  var inline = _ref.inline;
  return inline ? 'inline-flex' : 'flex';
}, column);

function _templateObject2() {
  var data = _taggedTemplateLiteralLoose(["\n\tfrom {\n\t\topacity: 0;\n    transform: translate(0,-30px);\n\t}\n  to {\n    opacity: 1;\n    transform: translate(0, 0);\n  }\n"]);

  _templateObject2 = function _templateObject2() {
    return data;
  };

  return data;
}

function _templateObject$2() {
  var data = _taggedTemplateLiteralLoose(["\n  0% {\n    opacity: 0;\n  }\n  10%, 90% {\n     transform: translate(-1px, 0);\n   }\n\n   20%, 80% {\n     transform: translate(2px, 0);\n   }\n\n   30%, 50%, 70% {\n     transform: translate(-4px, 0);\n   }\n\n   40%, 60% {\n     transform: translate(4px, 0);\n   }\n   100% {\n     transform: translate(0, 0);\n     opacity: 1;\n   }\n"]);

  _templateObject$2 = function _templateObject() {
    return data;
  };

  return data;
}
var shakeIn = /*#__PURE__*/keyframes( /*#__PURE__*/_templateObject$2());
var fadeIn = /*#__PURE__*/keyframes( /*#__PURE__*/_templateObject2());

function _templateObject3() {
  var data = _taggedTemplateLiteralLoose(["\n      animation: none;\n    "]);

  _templateObject3 = function _templateObject3() {
    return data;
  };

  return data;
}

function _templateObject2$1() {
  var data = _taggedTemplateLiteralLoose(["\n      border-color: ", ";\n      animation: ", " 600ms cubic-bezier(0.165, 0.84, 0.44, 1) both;\n    "]);

  _templateObject2$1 = function _templateObject2() {
    return data;
  };

  return data;
}

function _templateObject$3() {
  var data = _taggedTemplateLiteralLoose(["\n  border-radius: ", ";\n  border-width: 1px;\n  border-style: solid;\n  border-color: ", ";\n  animation: ", " 600ms cubic-bezier(0.165, 0.84, 0.44, 1) both;\n  ", "\n  ", "\n"]);

  _templateObject$3 = function _templateObject() {
    return data;
  };

  return data;
}
var Base = /*#__PURE__*/styled(Flex)( /*#__PURE__*/_templateObject$3(), function (props) {
  return props.theme.radii.md;
}, function (props) {
  return props.theme.colors.borderGray;
}, fadeIn, function (props) {
  return props.variant === 'error' && css(_templateObject2$1(), function (props) {
    return props.theme.colors.state.error;
  }, shakeIn);
}, function (props) {
  return props.noAnimation && css(_templateObject3());
});
var Alert = function Alert(_ref) {
  var color = _ref.color,
      children = _ref.children,
      props = _objectWithoutPropertiesLoose(_ref, ["color", "children"]);

  var variant = props.variant;
  return React.createElement(Base, Object.assign({}, props, {
    color: color,
    px: 4,
    py: 3,
    alignItems: "stretch"
  }), React.createElement(Box, {
    mt: "2px",
    mr: 3
  }, variant === 'error' && React.createElement(Warning, {
    color: "state.error",
    display: "block",
    size: 24
  }), variant === 'success' && React.createElement(CheckmarkAlt, {
    color: "primary",
    display: "block",
    size: 24
  }), variant === 'info' && React.createElement(InfoAlt, {
    color: "neutral.4",
    display: "block",
    size: 24
  })), React.createElement(Box, null, children));
};

function _templateObject$4() {
  var data = _taggedTemplateLiteralLoose(["\n  ", ";\n  ", "\n"]);

  _templateObject$4 = function _templateObject() {
    return data;
  };

  return data;
}
var whiteSpace = /*#__PURE__*/system({
  whiteSpace: true
});
var textTransform = /*#__PURE__*/system({
  textTransform: true
});
var textDecoration = /*#__PURE__*/system({
  textDecoration: true
});
var mutedAttrs = function mutedAttrs(props) {
  if (props.mute) {
    return {
      color: props.theme.colors.neutral[5]
    };
  }

  return {};
};
var Text = /*#__PURE__*/styled.div.attrs(function (props) {
  var retProps = _extends({}, getTypoStyles(props), {}, mutedAttrs(props), {
    mb: props.as === 'p' && !props.mb ? 'xs' : props.mb
  });

  if (props.bold) {
    retProps.fontWeight = props.theme.fontWeights.bold;
  }

  return retProps;
})( /*#__PURE__*/_templateObject$4(), /*#__PURE__*/rhythm(space$1), /*#__PURE__*/compose(typography, display, color, whiteSpace, textTransform, textDecoration, textShadow));
Text.defaultProps = {
  mt: 0
}; // Legacy, deprecated

/* eslint-disable react/display-name */
// @ts-ignore

Text.span = function (props) {
  return React.createElement(Text, Object.assign({
    as: "span"
  }, props));
}; // @ts-ignore


Text.a = function (props) {
  return React.createElement(Text, Object.assign({
    as: "a"
  }, props));
}; // @ts-ignore


Text.p = function (props) {
  return React.createElement(Text, Object.assign({
    as: "p"
  }, props));
}; // @ts-ignore


Text.em = function (props) {
  return React.createElement(Text, Object.assign({
    as: "em"
  }, props));
}; // @ts-ignore


Text.label = function (props) {
  return React.createElement(Text, Object.assign({
    as: "label"
  }, props));
}; // @ts-ignore


Text.time = function (props) {
  return React.createElement(Text, Object.assign({
    as: "time"
  }, props));
};
/* eslint-enable react/display-name */

function _templateObject5() {
  var data = _taggedTemplateLiteralLoose(["\n  ", "\n  ", "\n"]);

  _templateObject5 = function _templateObject5() {
    return data;
  };

  return data;
}

function _templateObject4() {
  var data = _taggedTemplateLiteralLoose(["\n  ", "\n  ", "\n"]);

  _templateObject4 = function _templateObject4() {
    return data;
  };

  return data;
}

function _templateObject3$1() {
  var data = _taggedTemplateLiteralLoose(["\n  appearance: none;\n  margin: 0;\n  padding: 0;\n  border: 0;\n  cursor: pointer;\n\n  /* Corrects font smoothing for webkit */\n  -webkit-font-smoothing: inherit;\n  -moz-osx-font-smoothing: inherit;\n"]);

  _templateObject3$1 = function _templateObject3() {
    return data;
  };

  return data;
}

function _templateObject2$2() {
  var data = _taggedTemplateLiteralLoose(["\n  ", ";\n  ", "\n  ", "\n  &:active {\n    outline: none;\n  }\n"]);

  _templateObject2$2 = function _templateObject2() {
    return data;
  };

  return data;
}

function _templateObject$5() {
  var data = _taggedTemplateLiteralLoose(["\n  text-decoration: ", ";\n  &:hover,\n  &:focus {\n    text-decoration: ", ";\n  }\n"]);

  _templateObject$5 = function _templateObject() {
    return data;
  };

  return data;
}

var textDecoration$1 = function textDecoration() {
  return css(_templateObject$5(), function (_ref) {
    var textDecoration = _ref.textDecoration;
    return textDecoration;
  }, function (_ref2) {
    var textDecoration = _ref2.textDecoration;
    return textDecoration === 'underline' ? 'none' : 'underline';
  });
};

var anchorCss = /*#__PURE__*/css( /*#__PURE__*/_templateObject2$2(), /*#__PURE__*/rhythm(space$1), /*#__PURE__*/compose(typography, display, color), textDecoration$1);

var anchorAttrs = function anchorAttrs(props) {
  var retProps = _extends({}, getTypoStyles(props), {}, mutedAttrs(props));

  if (!retProps.lineHeight) {
    retProps.lineHeight = 'inherit';
  }

  if (props.as === 'button') {
    warnOnce('<Anchor as="button"> is deprecated, use <AnchorButton> instead.');
    retProps = _extends({}, {
      fontSize: retProps.fontSize || 'inherit',
      lineHeight: retProps.lineHeight || 'normal',
      // @ts-ignore
      type: props.type || 'button',
      bg: props.bg || props.backgroundColor || 'transparent',
      color: 'link',
      textDecoration: 'underline'
    }, {}, retProps);
  }

  return retProps;
};

var buttonResetCss = /*#__PURE__*/css( /*#__PURE__*/_templateObject3$1());
var Anchor = /*#__PURE__*/styled.a.attrs(anchorAttrs)( /*#__PURE__*/_templateObject4(), function (props) {
  return props.as === 'button' && buttonResetCss;
}, anchorCss);
Anchor.defaultProps = {
  color: 'link',
  textDecoration: 'underline'
};
var AnchorButton = /*#__PURE__*/styled.button.attrs(function (props) {
  var retProps = anchorAttrs(props);

  if (!retProps.fontSize) {
    retProps.fontSize = 'inherit';
  }

  if (!retProps.lineHeight) {
    retProps.lineHeight = 'normal';
  }

  return retProps;
})( /*#__PURE__*/_templateObject5(), buttonResetCss, anchorCss);
AnchorButton.defaultProps = {
  type: 'button',
  color: 'link',
  textDecoration: 'underline',
  bg: 'transparent'
};

function _templateObject2$3() {
  var data = _taggedTemplateLiteralLoose(["\n      transform: rotate(", "deg);\n    "]);

  _templateObject2$3 = function _templateObject2() {
    return data;
  };

  return data;
}

function _templateObject$6() {
  var data = _taggedTemplateLiteralLoose(["\n  pointer-events: none;\n  ", ";\n"]);

  _templateObject$6 = function _templateObject() {
    return data;
  };

  return data;
}
var directionMap = {
  up: 180,
  down: 0,
  left: 90,
  right: -90
};
var Arrow = /*#__PURE__*/styled(Down)( /*#__PURE__*/_templateObject$6(), function (props) {
  var degrees = props.degrees || directionMap[props.direction || 'down'];
  return css(_templateObject2$3(), degrees);
});
Arrow.defaultProps = {
  width: 10,
  height: 10,
  bg: 'transparent'
};

function _templateObject3$2() {
  var data = _taggedTemplateLiteralLoose(["\n            ", "\n            ", "\n          "]);

  _templateObject3$2 = function _templateObject3() {
    return data;
  };

  return data;
}

function _templateObject2$4() {
  var data = _taggedTemplateLiteralLoose(["\n      * {\n        box-sizing: border-box;\n      }\n      html {\n        overflow-y: scroll;\n      }\n\n      body {\n        font-family: ", ";\n        color: ", ";\n        margin: 0;\n        text-rendering: optimizeLegibility;\n        -webkit-font-smoothing: antialiased;\n        -moz-osx-font-smoothing: grayscale;\n\n        ", "\n      }\n      a {\n        color: ", ";\n      }\n      ul,\n      ol {\n        margin: 0;\n      }\n    "]);

  _templateObject2$4 = function _templateObject2() {
    return data;
  };

  return data;
}

function _templateObject$7() {
  var data = _taggedTemplateLiteralLoose(["\n  ", "\n  ", "\n"]);

  _templateObject$7 = function _templateObject() {
    return data;
  };

  return data;
}
var path = 'https://assets.urbansharing.design/v1/fonts/urban-grotesk/';
var GlobalStyles = /*#__PURE__*/createGlobalStyle( /*#__PURE__*/_templateObject$7(), function (_ref) {
  var font = _ref.font;
  return font === 'urban-grotesk' && "\n      @font-face {\n        font-family: 'Urban Grotesk';\n        src: url('" + path + "urban_grotesk_bold-webfont.woff2') format('woff2'),\n          url('" + path + "urban_grotesk_bold-webfont.woff') format('woff');\n        font-weight: bold;\n        font-style: normal;\n        font-display: swap;\n      }\n\n      @font-face {\n        font-family: 'Urban Grotesk';\n        src: url('" + path + "urban_grotesk_bold-webfont.woff2') format('woff2'),\n          url('" + path + "urban_grotesk_bold-webfont.woff') format('woff');\n        font-weight: 700;\n        font-style: normal;\n        font-display: swap;\n      }\n\n      @font-face {\n        font-family: 'Urban Grotesk';\n        src: url('" + path + "urban_grotesk_italic-webfont.woff2') format('woff2'),\n          url('" + path + "urban_grotesk_italic-webfont.woff') format('woff');\n        font-weight: normal;\n        font-style: italic;\n        font-display: swap;\n      }\n\n      @font-face {\n        font-family: 'Urban Grotesk';\n        src: url('" + path + "urban_grotesk_medium-webfont.woff2') format('woff2'),\n          url('" + path + "urban_grotesk_medium-webfont.woff') format('woff');\n        font-weight: normal;\n        font-style: normal;\n        font-display: swap;\n      }\n  ";
}, function (_ref2) {
  var theme = _ref2.theme;
  return css(_templateObject2$4(), theme.fonts.sansSerif, theme.colors.text, function (_ref3) {
    var theme = _ref3.theme;
    var typoStyles = getTypoStyles({
      theme: theme,
      typoStyle: 'xs'
    });

    var props = _extends({}, typoStyles, {
      theme: theme
    });

    return css(_templateObject3$2(), fontSize(props), lineHeight(props));
  }, theme.colors.link);
});

// ref https://github.com/jxnblk/styled-system/pull/361.

var _breakpoints = ['32em', '40em', '48em', '64em', '80em'];
_breakpoints.xs = 0;
_breakpoints.sm = '32em';
_breakpoints.md = '40em';
_breakpoints.lg = '48em';
_breakpoints.xl = '64em';
_breakpoints.xxl = '80em';
var breakpoints = _breakpoints;
var mediaQueries = /*#__PURE__*/breakpoints.map(function (width) {
  return "@media screen and (min-width: " + width + ")";
});
var layers = {
  backdrop: 100,
  dialog: 200
};
var maxWidth = '1150px';
var shadowColor = 'rgba(0,0,0,0.08)';
var boxShadows = ["0 0 0 1px " + shadowColor, "0px 1px 2px rgba(0, 0, 0, 0.05), 0px 1px 7px rgba(0, 0, 0, 0.06)", "0 0 20px -6px " + shadowColor];
var shadows = {
  light: '0px 1px 2px rgba(0, 0, 0, 0.05), 0px 1px 7px rgba(0, 0, 0, 0.06);',
  heavy: '0px 0px 80px rgba(0, 0, 0, 0.08),0px 3px 10px rgba(0, 0, 0, 0.1);',
  inset: 'inset 0 1px 1px rgba(144,164,174,.15)',
  sm: boxShadows[0],
  md: boxShadows[1],
  lg: boxShadows[2]
};
var _fontSizes = [14, 16, 19, 26, 40, 56, 65, 85];
_fontSizes.body = _fontSizes[2];
var fontSizes = _fontSizes;
var textSize = _fontSizes[2];
var fontWeights = {
  normal: 500,
  bold: 700
};
var lineHeights = [1, 1.15, 1.45];
var space = [0, 4, 8, 12, 16, 24, 32, 64, 128];
var space2 = [0, 10, 20, 30, 40, 60, 80, 100, 140];
var rhythms = {
  xxs: space2[1],
  xs: space2[2],
  sm: [space2[3], space2[4]],
  md: [space2[4], space2[5]],
  lg: [space2[5], space2[7]],
  xl: [space2[6], space2[8]]
};
var typoStyles = {
  xxs: {
    fontSize: {
      xs: '14px',
      lg: '16px'
    },
    lineHeight: {
      xs: '20px',
      lg: '22px'
    },
    fontWeight: null
  },
  xs: {
    fontSize: {
      xs: '16px',
      lg: '20px'
    },
    lineHeight: {
      xs: '22px',
      lg: '27px'
    },
    fontWeight: null
  },
  sm: {
    fontSize: {
      xs: '18px',
      lg: '26px'
    },
    lineHeight: {
      xs: '27px',
      lg: '34px'
    },
    fontWeight: null
  },
  md: {
    fontSize: {
      xs: '25px',
      lg: '40px'
    },
    lineHeight: {
      xs: '29px',
      lg: '48px'
    },
    fontWeight: 'bold'
  },
  lg: {
    fontSize: {
      xs: '30px',
      lg: '48px'
    },
    lineHeight: {
      xs: '33px',
      lg: '55px'
    },
    fontWeight: 'bold'
  },
  xl: {
    fontSize: {
      xs: '37px',
      lg: '65px'
    },
    lineHeight: {
      xs: '45px',
      lg: '69px'
    },
    fontWeight: 'bold'
  },
  xxl: {
    fontSize: {
      xs: '45px',
      lg: '85px'
    },
    lineHeight: {
      xs: '53px',
      lg: '88px'
    },
    fontWeight: 'bold'
  }
};
var defaultColorKey = 'purple01';

function getColors(colorKey) {
  var themeColorTokens = themeColors[colorKey || defaultColorKey];
  var text = neutral.dark01;

  if (colorKey === 'bikeMi') {
    themeColorTokens.primaryIcon = themeColorTokens.primaryDark = themeColorTokens.primaryText = text = 'black';
  }

  return _extends({}, themeColorTokens, {
    text: text,
    borderGray: neutral.gray02,
    formGray: neutral.gray02b,
    neutral: [neutral.white, neutral.gray01, neutral.gray02, neutral.gray03, neutral.gray04, neutral.gray05, neutral.dark01],
    palette: palette,
    paletteLight: paletteLight,
    paletteDark: paletteDark,
    accent: accent,
    state: state
  });
}

var colors = /*#__PURE__*/getColors(defaultColorKey);

function getButtons(colors) {
  return {
    primary: {
      color: colors.primaryContrast,
      backgroundColor: colors.primary,
      borderColor: colors.primary,
      '&:hover,&:focus': {
        color: colors.primaryContrast,
        filter: 'brightness(0.85)'
      }
    },
    secondary: {
      color: colors.link,
      backgroundColor: colors.white,
      borderColor: colors.primary,
      '&:hover,&:focus': {
        borderColor: colors.primaryDark,
        color: colors.text
      }
    },
    secondaryNegative: {
      color: colors.primaryContrast,
      backgroundColor: 'transparent',
      borderColor: colors.primaryDark,
      '&:hover,&:focus': {
        borderColor: colors.primaryDark,
        color: colors.text
      }
    },
    primaryDark: {
      color: colors.primaryDarkContrast,
      backgroundColor: colors.primaryDark,
      borderColor: colors.primaryDark,
      '&:hover,&:focus': {
        backgroundColor: colors.primary,
        borderColor: colors.primary,
        color: colors.primaryContrast
      }
    },
    inverted: {
      color: colors.primaryDark,
      backgroundColor: colors.white,
      borderColor: colors.white,
      '&:hover,&:focus': {
        color: colors.primary
      }
    },
    link: {
      color: colors.link,
      textDecoration: 'underline',
      backgroundColor: 'transparent',
      borderColor: 'transparent',
      '&:hover,&:focus': {
        textDecoration: 'none'
      }
    },
    linkNegative: {
      color: colors.primaryContrast,
      backgroundColor: 'transparent',
      borderColor: 'transparent',
      '&:hover,&:focus': {
        borderColor: colors.primaryDark
      }
    },
    danger: {
      color: colors.white,
      backgroundColor: colors.state.error,
      borderColor: colors.state.error,
      '&:hover,&:focus': {
        color: colors.primaryContrast,
        filter: 'brightness(0.85)'
      }
    }
  };
}

var fontFamily = "'Urban Grotesk', 'Helvetica Neue', sans-serif";
var theme = {
  preset: 'default',
  name: defaultColorKey,
  maxWidth: maxWidth,
  fontSizes: fontSizes,
  textSize: textSize,
  typoStyles: typoStyles,
  lineHeights: lineHeights,
  fontWeights: fontWeights,
  bold: 700,
  space: space,
  space2: space2,
  rhythms: rhythms,
  radii: {
    none: 0,
    md: '6px',
    lg: '10px'
  },
  layers: layers,
  breakpoints: breakpoints,
  mediaQueries: mediaQueries,
  shadowColor: shadowColor,
  boxShadows: boxShadows,
  shadows: shadows,
  colors: colors,
  fonts: {
    sansSerif: fontFamily,
    heading: fontFamily,
    button: fontFamily,
    mono: 'Consolas, "Andale Mono WT", "Andale Mono", "Lucida Console", "Lucida Sans Typewriter", "DejaVu Sans Mono", "Bitstream Vera Sans Mono", "Liberation Mono", "Nimbus Mono L", Monaco, "Courier New", Courier, monospace'
  },
  buttons: /*#__PURE__*/getButtons(colors)
};
function getTheme(colorKey) {
  if (colorKey === void 0) {
    colorKey = defaultColorKey;
  }

  var colors = getColors(colorKey);
  var buttons = getButtons(colors);
  return _extends({}, theme, {
    name: colorKey,
    colors: colors,
    buttons: buttons
  });
}

var BaseStyles = function BaseStyles(_ref) {
  var colorKey = _ref.colorKey,
      children = _ref.children,
      theme = _ref.theme,
      font = _ref.font;
  return React.createElement(ThemeProvider, {
    theme: theme || getTheme(colorKey)
  }, React.createElement(React.Fragment, null, React.createElement(GlobalStyles, {
    font: font
  }), children));
};

function _templateObject$8() {
  var data = _taggedTemplateLiteralLoose(["\n  border: 0;\n  background: none;\n  overflow: visible;\n  -webkit-appearance: none; /* for input */\n  -moz-user-select: none;\n  user-select: none;\n"]);

  _templateObject$8 = function _templateObject() {
    return data;
  };

  return data;
}
var buttonResetCss$1 = /*#__PURE__*/css( /*#__PURE__*/_templateObject$8());
var MARGIN_REG = /^[m][trblxy]?$/;
var PADDING_REG = /^[p][trblxy]?$/;
var buttonResetAttrs = function buttonResetAttrs(props) {
  var p = props.p;
  var m = props.m;
  var bg = props.bg || 'none';
  var keys = Object.keys(props);

  if (keys.filter(function (key) {
    return PADDING_REG.test(key);
  }).length === 0) {
    p = 0;
  }

  if (keys.filter(function (key) {
    return MARGIN_REG.test(key);
  }).length === 0) {
    m = 0;
  }

  return {
    p: p,
    m: m,
    bg: bg
  };
};

function _templateObject4$1() {
  var data = _taggedTemplateLiteralLoose(["\n  ", "\n"]);

  _templateObject4$1 = function _templateObject4() {
    return data;
  };

  return data;
}

function _templateObject3$3() {
  var data = _taggedTemplateLiteralLoose(["\n  ", "\n"]);

  _templateObject3$3 = function _templateObject3() {
    return data;
  };

  return data;
}

function _templateObject2$5() {
  var data = _taggedTemplateLiteralLoose(["\n  vertical-align: middle;\n  text-decoration: none;\n  cursor: pointer;\n  user-select: none;\n  transition: 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275) all !important;\n  ", ";\n  ", ";\n  ", ";\n  ", "\n  ", ";\n"]);

  _templateObject2$5 = function _templateObject2() {
    return data;
  };

  return data;
}

function _templateObject$9() {
  var data = _taggedTemplateLiteralLoose(["\n    pointer-events: none;\n    cursor: default;\n    opacity: 0.5;\n  "]);

  _templateObject$9 = function _templateObject() {
    return data;
  };

  return data;
}
var COOL_PADDING_Y = 6; // from theme space scale

var COOL_PADDING_Y_SMALL = 4; // from theme space scale

var disabled = function disabled(props) {
  return props.disabled && css(_templateObject$9());
};

var resetted = function resetted(props) {
  return props.resetted && buttonResetCss$1;
};

var buttonCss = /*#__PURE__*/css( /*#__PURE__*/_templateObject2$5(), /*#__PURE__*/compose(layout, typography, color, border, shadow, /*#__PURE__*/system({
  pointerEvents: {
    property: 'pointerEvents'
  }
}), /*#__PURE__*/variant({
  prop: 'variant',
  scale: 'buttons'
}), /*#__PURE__*/variant({
  prop: 'size',
  scale: 'buttonSizes',
  variants: {
    small: {
      fontWeight: 'bold',
      fontSize: '14px',
      lineHeight: 2,
      textTransform: 'uppercase',
      letterSpacing: '1px',
      py: 1,
      px: 3,
      height: '2rem',
      mb: 1,
      mr: 1
    },
    medium: {
      fontSize: 2,
      lineHeight: 2,
      py: 2,
      px: 4,
      mb: 1,
      mr: 1
    }
  }
})), /*#__PURE__*/rhythm(space$1), disabled, function (_ref) {
  var variant = _ref.variant;
  return variant === 'reset' && buttonResetCss$1;
}, resetted);

function buttonAttrs(props) {
  if (props.resetted) {
    return buttonResetAttrs(props);
  }

  var theme = props.theme,
      shape = props.shape;
  var retProps = {
    shape: theme.preset === 'stationClient' ? 'cool' : shape,
    size: props.small ? 'small' : props.size
  };

  if (shape === 'cool') {
    var coolPadding = props.small || props.size === 'small' ? COOL_PADDING_Y_SMALL : COOL_PADDING_Y;
    var pl = props.pl || coolPadding;
    var pr = props.pr || coolPadding;
    return _extends({
      pl: pl,
      pr: pr,
      borderRadius: '9999px'
    }, retProps);
  }

  return retProps;
}

var Button = /*#__PURE__*/styled.button.attrs(buttonAttrs)( /*#__PURE__*/_templateObject3$3(), buttonCss);
Button.defaultProps = {
  display: 'inline-block',
  variant: 'secondary',
  shape: 'normal',
  size: 'medium',
  fontFamily: 'button',
  textAlign: 'center',
  borderWidth: 2,
  borderStyle: 'solid',
  borderRadius: 'md',
  type: 'button'
};
var ButtonAnchor = /*#__PURE__*/styled.a.attrs(buttonAttrs)( /*#__PURE__*/_templateObject4$1(), buttonCss);
ButtonAnchor.defaultProps = {
  display: 'inline-block',
  variant: 'secondary',
  shape: 'normal',
  size: 'medium',
  fontFamily: 'button',
  textAlign: 'center',
  borderWidth: 2,
  borderStyle: 'solid',
  borderRadius: 'md'
};

function _templateObject2$6() {
  var data = _taggedTemplateLiteralLoose(["\n  & > * {\n    margin-bottom: ", "px;\n  }\n  ", ";\n"]);

  _templateObject2$6 = function _templateObject2() {
    return data;
  };

  return data;
}

function _templateObject$a() {
  var data = _taggedTemplateLiteralLoose(["\n    & > button {\n      &:not(:first-child):not(:last-child) {\n        border-radius: 0;\n        border-right: 0;\n      }\n      &:first-child {\n        border-top-right-radius: 0;\n        border-bottom-right-radius: 0;\n        border-right: 0;\n      }\n      &:last-child {\n        border-top-left-radius: 0;\n        border-bottom-left-radius: 0;\n      }\n    }\n    & > * {\n      margin-right: 0;\n    }\n  "]);

  _templateObject$a = function _templateObject() {
    return data;
  };

  return data;
}

function segment(props) {
  if (!props.segment) {
    return;
  }

  return css(_templateObject$a());
}

var StyledButtonGroup = /*#__PURE__*/styled(Flex)( /*#__PURE__*/_templateObject2$6(), function (_ref) {
  var theme = _ref.theme;
  return theme.space[3];
}, segment);
var ButtonGroup = function ButtonGroup(_ref2) {
  var children = _ref2.children,
      color = _ref2.color,
      props = _objectWithoutPropertiesLoose(_ref2, ["children", "color"]);

  var theme = useStyledComponentsTheme();

  if (!children) {
    return null;
  }

  var childrenCount = React.Children.count(children);
  var modifiedChildren = props.segment ? React.Children.map(children, function (child, i) {
    if (!child) {
      return child;
    }

    if (!React.isValidElement(child)) {
      return child;
    } // add extra padding left/right on first and last button if button shape is cool


    if (child.props.shape !== 'cool') {
      return child;
    }

    var isFirst = i === 0;
    var isLast = i === childrenCount - 1;

    if (!isFirst && !isLast) {
      return child;
    }

    var padding = theme.space[COOL_PADDING_Y - 1];

    if (isFirst) {
      return React.cloneElement(child, {
        pl: padding
      });
    }

    if (isLast) {
      return React.cloneElement(child, {
        pr: padding
      });
    }

    return child;
  }) : children;
  return React.createElement(StyledButtonGroup, Object.assign({
    justifyContent: "flex-start",
    flexWrap: "wrap",
    flexDirection: "row",
    color: color
  }, props), modifiedChildren);
};

function _templateObject7() {
  var data = _taggedTemplateLiteralLoose(["\n  border: 0;\n  clip: rect(1px, 1px, 1px, 1px);\n  overflow: hidden;\n  padding: 0;\n  position: absolute;\n  white-space: nowrap;\n  opacity: 0;\n  &:focus {\n    & ~ ", " {\n      ", ";\n    }\n  }\n"]);

  _templateObject7 = function _templateObject7() {
    return data;
  };

  return data;
}

function _templateObject6() {
  var data = _taggedTemplateLiteralLoose(["\n          border-color: ", ";\n          box-shadow: 0px 0px 1px 1px ", ";\n        "]);

  _templateObject6 = function _templateObject6() {
    return data;
  };

  return data;
}

function _templateObject5$1() {
  var data = _taggedTemplateLiteralLoose(["\n      border-color: ", ";\n      ", "\n    "]);

  _templateObject5$1 = function _templateObject5() {
    return data;
  };

  return data;
}

function _templateObject4$2() {
  var data = _taggedTemplateLiteralLoose(["\n      background: ", ";\n      border: 1px solid transparent;\n    "]);

  _templateObject4$2 = function _templateObject4() {
    return data;
  };

  return data;
}

function _templateObject3$4() {
  var data = _taggedTemplateLiteralLoose(["\n  flex-shrink: 0;\n  background: ", ";\n  border-width: 1px;\n  border-style: solid;\n  border-color: ", ";\n  border-radius: 3px;\n  width: 20px;\n  height: 20px;\n  display: flex;\n  align-items: center;\n  justify-content: center;\n  margin-right: ", "px;\n  margin-top: ", "px;\n  opacity: ", ";\n  transition: border-color 150ms ease-out;\n  ", "\n"]);

  _templateObject3$4 = function _templateObject3() {
    return data;
  };

  return data;
}

function _templateObject2$7() {
  var data = _taggedTemplateLiteralLoose(["\n      color: ", ";\n    "]);

  _templateObject2$7 = function _templateObject2() {
    return data;
  };

  return data;
}

function _templateObject$b() {
  var data = _taggedTemplateLiteralLoose(["\n  display: flex;\n  align-items: flex-start;\n  width: 100%;\n  position: relative;\n  ", "\n"]);

  _templateObject$b = function _templateObject() {
    return data;
  };

  return data;
}
var Label = /*#__PURE__*/styled.label( /*#__PURE__*/_templateObject$b(), function (_ref) {
  var theme = _ref.theme,
      disabled = _ref.disabled;
  return disabled && css(_templateObject2$7(), theme.colors.neutral[4]);
});
var CheckboxIcon = /*#__PURE__*/styled.div( /*#__PURE__*/_templateObject3$4(), function (_ref2) {
  var theme = _ref2.theme,
      checked = _ref2.checked;
  return checked ? theme.colors.primary : theme.colors.white;
}, function (_ref3) {
  var theme = _ref3.theme,
      checked = _ref3.checked;
  return checked ? theme.colors.primary : theme.colors.neutral[4];
}, function (_ref4) {
  var theme = _ref4.theme;
  return theme.space[2];
}, function (_ref5) {
  var theme = _ref5.theme;
  return theme.space[1] / 2;
}, function (_ref6) {
  var disabled = _ref6.disabled;
  return disabled ? 0.5 : 1;
}, function (_ref7) {
  var alternative = _ref7.alternative,
      checked = _ref7.checked,
      theme = _ref7.theme;
  return alternative && css(_templateObject4$2(), checked ? theme.colors.primary : theme.colors.formGray);
});

function focusedCheckboxIcon(_ref8) {
  var disabled = _ref8.disabled,
      theme = _ref8.theme,
      checked = _ref8.checked;

  if (!disabled) {
    return css(_templateObject5$1(), theme.colors.primary, checked && css(_templateObject6(), theme.colors.white, theme.colors.primary));
  }

  return;
}

var HiddenCheckbox = /*#__PURE__*/styled.input( /*#__PURE__*/_templateObject7(), CheckboxIcon, focusedCheckboxIcon);

var CheckboxStateless = function CheckboxStateless(_ref) {
  var label = _ref.label,
      name = _ref.name,
      children = _ref.children,
      _ref$checked = _ref.checked,
      checked = _ref$checked === void 0 ? false : _ref$checked,
      _ref$disabled = _ref.disabled,
      disabled = _ref$disabled === void 0 ? false : _ref$disabled,
      onChange = _ref.onChange,
      alternative = _ref.alternative,
      props = _objectWithoutPropertiesLoose(_ref, ["label", "name", "children", "checked", "disabled", "onChange", "alternative"]);

  return React.createElement(Label, {
    disabled: disabled
  }, React.createElement(HiddenCheckbox, Object.assign({
    name: name,
    type: "checkbox",
    checked: checked,
    onChange: onChange,
    disabled: disabled
  }, props)), React.createElement(CheckboxIcon, {
    checked: checked,
    disabled: disabled,
    alternative: alternative
  }, checked && React.createElement(Check, {
    color: "white",
    size: 14
  })), label || children);
};

var Checkbox = function Checkbox(_ref) {
  var _onChange = _ref.onChange,
      checked = _ref.checked,
      alternative = _ref.alternative,
      label = _ref.label,
      children = _ref.children,
      _ref$disabled = _ref.disabled,
      disabled = _ref$disabled === void 0 ? false : _ref$disabled,
      props = _objectWithoutPropertiesLoose(_ref, ["onChange", "checked", "alternative", "label", "children", "disabled"]);

  var _useState = useState(checked || false),
      isChecked = _useState[0],
      setIsChecked = _useState[1];

  return React.createElement(CheckboxStateless, Object.assign({
    onChange: function onChange(event) {
      if (disabled) {
        return;
      }

      if (_onChange) {
        _onChange({
          event: event,
          isChecked: !isChecked
        });
      }

      setIsChecked(!isChecked);
    },
    checked: isChecked,
    alternative: alternative,
    disabled: disabled
  }, props), label || children);
};

function _templateObject$c() {
  var data = _taggedTemplateLiteralLoose(["\n  width: ", ";\n  max-width: ", ";\n"]);

  _templateObject$c = function _templateObject() {
    return data;
  };

  return data;
}
var Container = /*#__PURE__*/styled(Box)( /*#__PURE__*/_templateObject$c(), function (_ref) {
  var bleed = _ref.bleed;
  return bleed ? 'auto' : '90%';
}, function (_ref2) {
  var variant = _ref2.variant,
      fullWidth = _ref2.fullWidth,
      theme = _ref2.theme;

  if (variant === 'narrow') {
    return '600px';
  }

  if (variant === 'wide') {
    return '1400px';
  }

  return fullWidth ? 'none' : theme.maxWidth;
});
Container.defaultProps = {
  mx: ['auto']
};

function _templateObject$d() {
  var data = _taggedTemplateLiteralLoose(["\n  ", ";\n"]);

  _templateObject$d = function _templateObject() {
    return data;
  };

  return data;
}
var CssGrid = /*#__PURE__*/styled(Box)( /*#__PURE__*/_templateObject$d(), grid);
CssGrid.defaultProps = {
  display: 'grid'
};

function _templateObject3$5() {
  var data = _taggedTemplateLiteralLoose([""]);

  _templateObject3$5 = function _templateObject3() {
    return data;
  };

  return data;
}

function _templateObject2$8() {
  var data = _taggedTemplateLiteralLoose([""]);

  _templateObject2$8 = function _templateObject2() {
    return data;
  };

  return data;
}

function _templateObject$e() {
  var data = _taggedTemplateLiteralLoose([""]);

  _templateObject$e = function _templateObject() {
    return data;
  };

  return data;
}
var Heading = /*#__PURE__*/styled(Text).attrs(function (props) {
  var fontSize;

  switch (props.displaySize) {
    case 1:
      fontSize = [4, 4, 5, 6, 7];
      break;

    case 2:
      fontSize = [3, 3, 4, 5, 6];
      break;
  }

  if (fontSize) {
    return {
      fontSize: fontSize
    };
  }

  return props;
})( /*#__PURE__*/_templateObject$e());
Heading.defaultProps = {
  as: 'h1',
  mt: 0,
  lineHeight: 1,
  fontWeight: 'bold',
  fontFamily: 'heading',
  typoStyle: 'xl',
  mb: 'sm'
};
var H1 = Heading;
var H2 = /*#__PURE__*/styled(Heading)( /*#__PURE__*/_templateObject2$8());
H2.defaultProps = {
  as: 'h2',
  mt: 0,
  lineHeight: 1,
  fontWeight: 'bold',
  fontFamily: 'heading',
  typoStyle: 'sm',
  mb: 'xxs'
};
var H3 = /*#__PURE__*/styled(Heading)( /*#__PURE__*/_templateObject3$5());
H3.defaultProps = {
  as: 'h3',
  mt: 0,
  lineHeight: 1,
  fontWeight: 'bold',
  fontFamily: 'heading',
  typoStyle: 'xs',
  mb: 'xxs'
};

function _templateObject5$2() {
  var data = _taggedTemplateLiteralLoose(["\n  ", ";\n  position: relative;\n  background: white;\n  animation: ", " 200ms cubic-bezier(0.165, 0.84, 0.44, 1) both;\n"]);

  _templateObject5$2 = function _templateObject5() {
    return data;
  };

  return data;
}

function _templateObject4$3() {
  var data = _taggedTemplateLiteralLoose(["\n  position: fixed;\n  top: 0;\n  right: 0;\n  bottom: 0;\n  left: 0;\n  z-index: ", ";\n  overflow: auto;\n  outline: 0;\n  ", " {\n    display: flex;\n    justify-content: center;\n    align-items: center;\n  }\n"]);

  _templateObject4$3 = function _templateObject4() {
    return data;
  };

  return data;
}

function _templateObject3$6() {
  var data = _taggedTemplateLiteralLoose(["\n  background: ", ";\n  bottom: 0;\n  left: 0;\n  position: fixed;\n  right: 0;\n  top: 0;\n  opacity: 0.4;\n  z-index: ", ";\n  animation: ", " 300ms cubic-bezier(0.165, 0.84, 0.44, 1) both;\n"]);

  _templateObject3$6 = function _templateObject3() {
    return data;
  };

  return data;
}

function _templateObject2$9() {
  var data = _taggedTemplateLiteralLoose(["\n\tfrom {\n\t\ttransform: scale(0.5)\n\t}\n"]);

  _templateObject2$9 = function _templateObject2() {
    return data;
  };

  return data;
}

function _templateObject$f() {
  var data = _taggedTemplateLiteralLoose(["\n\tfrom {\n\t\topacity: 0;\n\t}\n"]);

  _templateObject$f = function _templateObject() {
    return data;
  };

  return data;
}
var fadeIn$1 = /*#__PURE__*/keyframes( /*#__PURE__*/_templateObject$f());
var scaleIn = /*#__PURE__*/keyframes( /*#__PURE__*/_templateObject2$9());
var DialogBackdrop = /*#__PURE__*/styled.div( /*#__PURE__*/_templateObject3$6(), function (_ref) {
  var theme = _ref.theme;
  return theme.colors.neutral[5];
}, function (_ref2) {
  var theme = _ref2.theme;
  return theme.layers.backdrop;
}, fadeIn$1);
var DialogWrapper = /*#__PURE__*/styled.div( /*#__PURE__*/_templateObject4$3(), function (_ref3) {
  var theme = _ref3.theme;
  return theme.layers.dialog;
}, function (_ref4) {
  var theme = _ref4.theme;
  return theme.mediaQueries[0];
});
var defaultVariant = {
  my: [null, 5],
  mx: [null, 4],
  borderRadius: [null, 'lg'],
  height: ['100vh', 'auto'],
  maxHeight: ['100vh', '90vh'],
  boxShadow: [null, 'heavy']
};
var DialogWindow = /*#__PURE__*/styled.div( /*#__PURE__*/_templateObject5$2(), /*#__PURE__*/compose(space$1, borderRadius, layout, boxShadow, /*#__PURE__*/variant({
  prop: 'size',
  variants: {
    fullscreen: {
      maxWidth: 'auto',
      width: '100%',
      height: '100vh'
    },
    // note: object notation does not work in variants
    // ref https://github.com/styled-system/styled-system/issues/1255#issuecomment-627439300
    medium: /*#__PURE__*/_extends({}, defaultVariant, {
      maxWidth: 800
    }),
    large: /*#__PURE__*/_extends({}, defaultVariant, {
      width: 1200
    })
  }
})), scaleIn);
DialogWindow.defaultProps = {
  size: 'medium',
  p: {
    _: 5,
    sm: 6
  },
  overflow: 'auto'
};

var DialogStateless = function DialogStateless(_ref) {
  var children = _ref.children,
      size = _ref.size,
      heading = _ref.heading,
      showCloseButton = _ref.showCloseButton,
      onCloseClick = _ref.onCloseClick,
      _ref$onClick = _ref.onClick,
      onClick = _ref$onClick === void 0 ? function () {} : _ref$onClick;
  return React.createElement(DialogWindow, {
    onClick: onClick,
    size: size
  }, React.createElement(Flex, {
    mb: 2,
    alignItems: "flex-start"
  }, React.createElement(Box, {
    flex: 1
  }, heading && React.createElement(H2, null, heading)), showCloseButton && React.createElement(Box, null, React.createElement(Button, {
    variant: "reset",
    px: 1,
    py: 1,
    "aria-label": "Close",
    onClick: onCloseClick
  }, React.createElement(RemoveAlt, null)))), React.createElement("div", null, children));
};

// Shamelessly stolen from https://github.com/priceline/design-system/blob/39beb570538f22489424b2f3ce355dc7c7cd8b37/packages/modal/test/ScrollLock.js
var ScrollLock = /*#__PURE__*/function () {
  function ScrollLock() {
    this.currentPos = 0;
    this.tag = null;
    this.head = document.head;
  }

  var _proto = ScrollLock.prototype;

  _proto.getCurrentPos = function getCurrentPos() {
    return window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop;
  };

  _proto.createStyleTag = function createStyleTag() {
    var css = "\n        body {\n          overflow: hidden !important;\n          position: fixed;\n          top: -" + this.currentPos + "px;\n          left: 0;\n          width: 100%;\n        }\n      ";
    var tag = document.createElement('style');
    tag.type = 'text/css';
    tag.setAttribute('data-react-scrolllock', '');
    tag.appendChild(document.createTextNode(css));

    if (this.head) {
      this.head.appendChild(tag);
      this.tag = tag;
    }
  };

  _proto.on = function on() {
    if (!this.head) return;
    this.currentPos = this.getCurrentPos();
    this.createStyleTag();
  };

  _proto.off = function off() {
    if (!this.tag || !this.head) return;
    this.head.removeChild(this.tag);
    this.tag = null;
    window.scrollTo(0, this.currentPos);
  };

  return ScrollLock;
}();

var isFunction = function isFunction(obj) {
  return typeof obj === 'function';
};

var Dialog = /*#__PURE__*/React.forwardRef(function Dialog(_ref, ref) {
  var children = _ref.children,
      heading = _ref.heading,
      renderOpenButton = _ref.renderOpenButton,
      _ref$closeOnOutsideCl = _ref.closeOnOutsideClick,
      closeOnOutsideClick = _ref$closeOnOutsideCl === void 0 ? true : _ref$closeOnOutsideCl,
      _ref$closeOnEsc = _ref.closeOnEsc,
      closeOnEsc = _ref$closeOnEsc === void 0 ? true : _ref$closeOnEsc,
      _ref$lockBodyScroll = _ref.lockBodyScroll,
      lockBodyScroll = _ref$lockBodyScroll === void 0 ? true : _ref$lockBodyScroll,
      _ref$showCloseButton = _ref.showCloseButton,
      showCloseButton = _ref$showCloseButton === void 0 ? true : _ref$showCloseButton,
      _ref$initialIsOpen = _ref.initialIsOpen,
      initialIsOpen = _ref$initialIsOpen === void 0 ? false : _ref$initialIsOpen,
      size = _ref.size,
      onClose = _ref.onClose,
      onOpen = _ref.onOpen,
      props = _objectWithoutPropertiesLoose(_ref, ["children", "heading", "renderOpenButton", "closeOnOutsideClick", "closeOnEsc", "lockBodyScroll", "showCloseButton", "initialIsOpen", "size", "onClose", "onOpen"]);

  var _usePortal = usePortal({
    closeOnOutsideClick: closeOnOutsideClick,
    closeOnEsc: closeOnEsc,
    onOpen: onOpen,
    onClose: onClose,
    isOpen: initialIsOpen
  }),
      openPortal = _usePortal.openPortal,
      closePortal = _usePortal.closePortal,
      isOpen = _usePortal.isOpen,
      Portal = _usePortal.Portal;

  React.useEffect(function () {
    if (!lockBodyScroll || !isOpen) {
      return;
    }

    var scrollLock = new ScrollLock();
    scrollLock.on();
    return function () {
      scrollLock.off();
    };
  }, [isOpen, lockBodyScroll]);
  React.useEffect(function () {
    if (ref) {
      // @ts-ignore
      ref.current = {
        openPortal: openPortal,
        closePortal: closePortal,
        isOpen: isOpen
      };
    }
  }, [openPortal, closePortal, isOpen, ref]);
  return React.createElement(React.Fragment, null, renderOpenButton && renderOpenButton(openPortal), isOpen && React.createElement(Portal, null, React.createElement(DialogWrapper, {
    "data-testid": props['data-testid'],
    onClick: function onClick(event) {
      if (closeOnOutsideClick) {
        closePortal(event);
      }
    }
  }, React.createElement(DialogStateless, {
    heading: heading,
    showCloseButton: showCloseButton,
    onCloseClick: closePortal,
    onClick: function onClick(event) {
      return event.stopPropagation();
    },
    size: size
  }, isFunction(children) ? children({
    closePortal: closePortal,
    isOpen: isOpen
  }) : children)), React.createElement(DialogBackdrop, null)));
});

function _templateObject$g() {
  var data = _taggedTemplateLiteralLoose(["\n  height: 16px;\n  border-radius: 50%;\n"]);

  _templateObject$g = function _templateObject() {
    return data;
  };

  return data;
}
var Base$1 = /*#__PURE__*/styled(Box)( /*#__PURE__*/_templateObject$g());
Base$1.defaultProps = {
  bg: 'neutral.3',
  width: 16,
  height: 16,
  display: 'inline-block'
};
var Dot = function Dot(_ref) {
  var children = _ref.children,
      bg = _ref.bg;

  if (!children) {
    return React.createElement(Base$1, {
      bg: bg
    });
  }

  return React.createElement(Flex, {
    alignItems: "center"
  }, React.createElement(Base$1, {
    bg: bg,
    mr: 2
  }), React.createElement(Box, null, children));
};

function _templateObject4$4() {
  var data = _taggedTemplateLiteralLoose(["\n      &:hover,\n      &:focus {\n        border-color: ", ";\n      }\n    "]);

  _templateObject4$4 = function _templateObject4() {
    return data;
  };

  return data;
}

function _templateObject3$7() {
  var data = _taggedTemplateLiteralLoose(["\n      border-color: transparent;\n      background: ", ";\n    "]);

  _templateObject3$7 = function _templateObject3() {
    return data;
  };

  return data;
}

function _templateObject2$a() {
  var data = _taggedTemplateLiteralLoose(["\n      background: ", ";\n      color: ", ";\n      font-style: italic;\n    "]);

  _templateObject2$a = function _templateObject2() {
    return data;
  };

  return data;
}

function _templateObject$h() {
  var data = _taggedTemplateLiteralLoose(["\n  ", ";\n  ", ";\n  ", ";\n  transition: border-color 150ms ease-out;\n  &::placeholder {\n    color: ", ";\n  }\n  &:focus {\n    outline: none;\n    border-color: ", ";\n  }\n"]);

  _templateObject$h = function _templateObject() {
    return data;
  };

  return data;
}
var base = /*#__PURE__*/css( /*#__PURE__*/_templateObject$h(), function (_ref) {
  var disabled = _ref.disabled,
      theme = _ref.theme;
  return disabled && css(_templateObject2$a(), theme.colors.neutral[1], theme.colors.neutral[5]);
}, function (_ref2) {
  var alternative = _ref2.alternative,
      disabled = _ref2.disabled,
      theme = _ref2.theme;
  return alternative && css(_templateObject3$7(), disabled ? theme.colors.neutral[1] : theme.colors.formGray);
}, function (_ref3) {
  var interactive = _ref3.interactive,
      theme = _ref3.theme;
  return interactive && css(_templateObject4$4(), theme.colors.primary);
}, function (props) {
  return props.theme.colors.neutral[4];
}, function (props) {
  return props.theme.colors.primary;
});
var inputDefaultProps = {
  px: 4,
  py: 3,
  width: 1,
  fontFamily: 'sansSerif',
  fontSize: 'body',
  lineHeight: 1,
  borderRadius: 'md',
  borderColor: 'neutral.4',
  borderStyle: 'solid',
  borderWidth: 1,
  bg: 'white',
  color: 'text'
};

function _templateObject2$b() {
  var data = _taggedTemplateLiteralLoose(["\n  ", "\n"]);

  _templateObject2$b = function _templateObject2() {
    return data;
  };

  return data;
}

function _templateObject$i() {
  var data = _taggedTemplateLiteralLoose(["\n  ", ";\n  ", ";\n"]);

  _templateObject$i = function _templateObject() {
    return data;
  };

  return data;
}
var inputCss = /*#__PURE__*/css( /*#__PURE__*/_templateObject$i(), /*#__PURE__*/compose(border, color, space$1, typography, width, maxWidth$1, height), base);
var Input = /*#__PURE__*/styled.input.attrs(function (props) {
  return {
    px: props.inputSize === 'small' ? 2 : props.px,
    py: props.inputSize === 'small' ? 1 : props.py,
    fontSize: props.inputSize === 'small' ? 16 : props.fontSize
  };
})( /*#__PURE__*/_templateObject2$b(), inputCss);
Input.defaultProps = inputDefaultProps;

function _templateObject8() {
  var data = _taggedTemplateLiteralLoose(["\n  min-width: 10rem;\n  animation: 100ms ease-out ", ";\n  animation-fill-mode: forwards;\n  animation-iteration-count: 1;\n  box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.12);\n  overflow: hidden;\n"]);

  _templateObject8 = function _templateObject8() {
    return data;
  };

  return data;
}

function _templateObject7$1() {
  var data = _taggedTemplateLiteralLoose(["\n  border-width: 0 0 1px;\n  border-color: ", ";\n  border-radius: 0;\n"]);

  _templateObject7$1 = function _templateObject7() {
    return data;
  };

  return data;
}

function _templateObject6$1() {
  var data = _taggedTemplateLiteralLoose(["\n  border-bottom: 1px solid ", ";\n  text-decoration: none;\n  display: block;\n  position: relative;\n  z-index: 1;\n"]);

  _templateObject6$1 = function _templateObject6() {
    return data;
  };

  return data;
}

function _templateObject5$3() {
  var data = _taggedTemplateLiteralLoose(["\n  ", ";\n"]);

  _templateObject5$3 = function _templateObject5() {
    return data;
  };

  return data;
}

function _templateObject4$5() {
  var data = _taggedTemplateLiteralLoose(["\n  border: none;\n  margin: 0;\n  padding: 0;\n  width: auto;\n  overflow: visible;\n\n  background: transparent;\n\n  /* inherit font & color from ancestor */\n  color: inherit;\n  font: inherit;\n\n  /* Normalize line-height. Cannot be changed from `normal` in Firefox 4+. */\n  line-height: normal;\n\n  /* Corrects font smoothing for webkit */\n  -webkit-font-smoothing: inherit;\n  -moz-osx-font-smoothing: inherit;\n\n  /* Corrects inability to style clickable `input` types in iOS */\n  -webkit-appearance: none;\n\n  ", ";\n"], ["\n  border: none;\n  margin: 0;\n  padding: 0;\n  width: auto;\n  overflow: visible;\n\n  background: transparent;\n\n  /* inherit font & color from ancestor */\n  color: inherit;\n  font: inherit;\n\n  /* Normalize line-height. Cannot be changed from \\`normal\\` in Firefox 4+. */\n  line-height: normal;\n\n  /* Corrects font smoothing for webkit */\n  -webkit-font-smoothing: inherit;\n  -moz-osx-font-smoothing: inherit;\n\n  /* Corrects inability to style clickable \\`input\\` types in iOS */\n  -webkit-appearance: none;\n\n  ", ";\n"]);

  _templateObject4$5 = function _templateObject4() {
    return data;
  };

  return data;
}

function _templateObject3$8() {
  var data = _taggedTemplateLiteralLoose(["\n      background-color: ", ";\n      font-weight: bold;\n    "]);

  _templateObject3$8 = function _templateObject3() {
    return data;
  };

  return data;
}

function _templateObject2$c() {
  var data = _taggedTemplateLiteralLoose(["\n  ", ";\n  border: none;\n  text-decoration: none;\n  display: block;\n  width: 100%;\n  text-align: left;\n  position: relative;\n  z-index: 1;\n  &:hover,\n  &:focus {\n    background-color: ", ";\n  }\n  ", ";\n"]);

  _templateObject2$c = function _templateObject2() {
    return data;
  };

  return data;
}

function _templateObject$j() {
  var data = _taggedTemplateLiteralLoose(["\n  from {\n    opacity: 0;\n    transform: translateY(-15px)\n  }\n  to {\n    opacity: 1;\n    transform: translateY(1px)\n  }\n"]);

  _templateObject$j = function _templateObject() {
    return data;
  };

  return data;
}
var dropdownAnimation = /*#__PURE__*/keyframes( /*#__PURE__*/_templateObject$j());
var dropdownItemCss = /*#__PURE__*/css( /*#__PURE__*/_templateObject2$c(), /*#__PURE__*/compose(typography, color, space$1), function (_ref) {
  var theme = _ref.theme;
  return theme.colors.neutral[1];
}, function (_ref2) {
  var theme = _ref2.theme,
      selected = _ref2.selected;
  return selected && css(_templateObject3$8(), theme.colors.neutral[1]);
});
var DropdownItem = /*#__PURE__*/styled.button( /*#__PURE__*/_templateObject4$5(), dropdownItemCss);
var defaultProps = {
  px: 3,
  py: 2,
  color: 'text'
};
DropdownItem.defaultProps = defaultProps;
var DropdownItemLink = /*#__PURE__*/styled.a( /*#__PURE__*/_templateObject5$3(), dropdownItemCss);
DropdownItemLink.defaultProps = defaultProps;
var DropdownCaption = /*#__PURE__*/styled(Box)( /*#__PURE__*/_templateObject6$1(), function (_ref3) {
  var theme = _ref3.theme;
  return theme.colors.borderGray;
});
DropdownCaption.defaultProps = {
  px: 3,
  py: 2,
  color: 'neutral.5'
};
var DropdownInput = /*#__PURE__*/styled(Input)( /*#__PURE__*/_templateObject7$1(), function (_ref4) {
  var theme = _ref4.theme;
  return theme.colors.neutral[2];
});
var Dropdown = /*#__PURE__*/styled(Box)( /*#__PURE__*/_templateObject8(), dropdownAnimation);
Dropdown.defaultProps = {
  bg: 'white',
  width: 1,
  position: 'absolute',
  right: 0,
  top: '100%',
  zIndex: 4,
  borderRadius: 'md'
}; // @ts-ignore

Dropdown.Item = DropdownItem; // @ts-ignore

Dropdown.Caption = DropdownCaption; // @ts-ignore

Dropdown.Input = DropdownInput;

function _templateObject$k() {
  var data = _taggedTemplateLiteralLoose([""]);

  _templateObject$k = function _templateObject() {
    return data;
  };

  return data;
}
var Label$1 = /*#__PURE__*/styled(Text).attrs(function (props) {
  var retProps = {
    mt: props.as === 'p' && props.mb
  };
  return retProps;
})( /*#__PURE__*/_templateObject$k());
Label$1.defaultProps = {
  as: 'label',
  mb: 2,
  display: 'block',
  typoStyle: 'xxs',
  color: 'neutral.5'
};

function _templateObject$l() {
  var data = _taggedTemplateLiteralLoose(["\n  animation: ", " 600ms cubic-bezier(0.165, 0.84, 0.44, 1) both;\n"]);

  _templateObject$l = function _templateObject() {
    return data;
  };

  return data;
}
var ErrorWrapper = /*#__PURE__*/styled(Flex)( /*#__PURE__*/_templateObject$l(), shakeIn);
var FormField = function FormField(_ref) {
  var _ref$showLabel = _ref.showLabel,
      showLabel = _ref$showLabel === void 0 ? true : _ref$showLabel,
      label = _ref.label,
      children = _ref.children,
      forId = _ref.forId,
      _ref$wrapperProps = _ref.wrapperProps,
      wrapperProps = _ref$wrapperProps === void 0 ? {
    mb: 'sm'
  } : _ref$wrapperProps,
      _ref$labelProps = _ref.labelProps,
      labelProps = _ref$labelProps === void 0 ? {} : _ref$labelProps,
      description = _ref.description,
      errorMessage = _ref.errorMessage,
      props = _objectWithoutPropertiesLoose(_ref, ["showLabel", "label", "children", "forId", "wrapperProps", "labelProps", "description", "errorMessage"]);

  var fieldChild = null;
  var LabelChild;
  var fieldId;
  React.Children.forEach(children, function (child) {
    if (child) {
      // @ts-ignore
      fieldChild = child; // @ts-ignore

      fieldId = forId ? forId : child.props.id;
    }
  });

  if (label) {
    LabelChild = React.createElement(Label$1, Object.assign({}, labelProps), label);
  }

  return React.createElement(Box, Object.assign({}, wrapperProps), showLabel && LabelChild && React.cloneElement(LabelChild, {
    htmlFor: fieldId
  }), errorMessage && React.createElement(ErrorWrapper, {
    justifyContent: "flex-start",
    mb: "xxs",
    color: "state.error"
  }, React.createElement(Warning, {
    mr: 2
  }), React.createElement(Text, {
    as: "span",
    typoStyle: "xs"
  }, errorMessage)), fieldChild && React.cloneElement(fieldChild, _extends({
    'aria-label': !showLabel && label ? label : null
  }, props)), description && React.createElement(Text, {
    typoStyle: "xxs",
    mt: "xxs"
  }, description));
};

var GUTTER_SPACE = 3;
var Grid = function Grid(_ref) {
  var color = _ref.color,
      props = _objectWithoutPropertiesLoose(_ref, ["color"]);

  return React.createElement(Flex, Object.assign({
    flexWrap: "wrap",
    color: color,
    mx: -GUTTER_SPACE
  }, props));
};
var GridCell = function GridCell(_ref2) {
  var color = _ref2.color,
      props = _objectWithoutPropertiesLoose(_ref2, ["color"]);

  return React.createElement(Box, Object.assign({
    px: GUTTER_SPACE,
    color: color
  }, props));
};

function _templateObject4$6() {
  var data = _taggedTemplateLiteralLoose(["\n  ", " ", " ", " ", " ", " ", ";\n"]);

  _templateObject4$6 = function _templateObject4() {
    return data;
  };

  return data;
}

function _templateObject3$9() {
  var data = _taggedTemplateLiteralLoose(["\n        ", " {\n          ", "\n        }\n      "]);

  _templateObject3$9 = function _templateObject3() {
    return data;
  };

  return data;
}

function _templateObject2$d() {
  var data = _taggedTemplateLiteralLoose(["\n  display: none;\n"]);

  _templateObject2$d = function _templateObject2() {
    return data;
  };

  return data;
}

function _templateObject$m() {
  var data = _taggedTemplateLiteralLoose(["\n  position: absolute;\n  width: 1px;\n  height: 1px;\n  padding: 0;\n  overflow: hidden;\n  clip: rect(0, 0, 0, 0);\n  whitespace: nowrap;\n  border: 0;\n"]);

  _templateObject$m = function _templateObject() {
    return data;
  };

  return data;
}

var getMaxWidth = function getMaxWidth(em) {
  return emToPx(em) - 0.01 + "em";
};

var breakpoints$1 = function breakpoints(props) {
  return {
    all: '',
    xs: "@media screen and (max-width: " + getMaxWidth(props.theme.breakpoints[0]) + ")",
    sm: "@media screen and (min-width: " + props.theme.breakpoints[0] + ") and (max-width: " + getMaxWidth(props.theme.breakpoints[1]) + ")",
    md: "@media screen and (min-width: " + props.theme.breakpoints[1] + ") and (max-width: " + getMaxWidth(props.theme.breakpoints[2]) + ")",
    lg: "@media screen and (min-width: " + props.theme.breakpoints[2] + ") and (max-width: " + getMaxWidth(props.theme.breakpoints[3]) + ")",
    xl: "@media screen and (min-width: " + props.theme.breakpoints[3] + ")"
  };
};

var srOnlyCss = /*#__PURE__*/css( /*#__PURE__*/_templateObject$m());
var hiddenCss = /*#__PURE__*/css( /*#__PURE__*/_templateObject2$d());

var hidden = function hidden(key) {
  return function (props) {
    return props[key] ? css(_templateObject3$9(), breakpoints$1(props)[key], props.srOnly ? srOnlyCss : hiddenCss) : null;
  };
};

var Hide = /*#__PURE__*/styled(Box)( /*#__PURE__*/_templateObject4$6(), /*#__PURE__*/hidden('all'), /*#__PURE__*/hidden('xs'), /*#__PURE__*/hidden('sm'), /*#__PURE__*/hidden('md'), /*#__PURE__*/hidden('lg'), /*#__PURE__*/hidden('xl'));

function _templateObject3$a() {
  var data = _taggedTemplateLiteralLoose(["\n  ", ";\n  ", ";\n"]);

  _templateObject3$a = function _templateObject3() {
    return data;
  };

  return data;
}

function _templateObject2$e() {
  var data = _taggedTemplateLiteralLoose(["\n    border-radius: 50%;\n  "]);

  _templateObject2$e = function _templateObject2() {
    return data;
  };

  return data;
}

function _templateObject$n() {
  var data = _taggedTemplateLiteralLoose(["\n    padding: 0;\n    width: 38px;\n    height: 38px;\n  "]);

  _templateObject$n = function _templateObject() {
    return data;
  };

  return data;
}

var iconOnly = function iconOnly(props) {
  return props.iconOnly && css(_templateObject$n());
};

var rounded = function rounded(props) {
  return props.rounded && css(_templateObject2$e());
};

var Base$2 = /*#__PURE__*/styled(Button)( /*#__PURE__*/_templateObject3$a(), iconOnly, rounded);
var IconButton = function IconButton(_ref) {
  var Icon = _ref.Icon,
      _ref$placement = _ref.placement,
      placement = _ref$placement === void 0 ? 'right' : _ref$placement,
      rounded = _ref.rounded,
      color = _ref.color,
      children = _ref.children,
      props = _objectWithoutPropertiesLoose(_ref, ["Icon", "placement", "rounded", "color", "children"]);

  var icon = Icon && React.createElement(Icon, {
    size: "1em"
  });
  var margin = 2;
  return React.createElement(Base$2, Object.assign({}, props, {
    color: color,
    iconOnly: !children,
    rounded: rounded,
    type: !props.as || props.as === 'button' ? 'button' : undefined
  }), React.createElement(Flex, {
    alignItems: "center",
    justifyContent: "center"
  }, placement === 'left' && icon, children && React.createElement(Box, {
    as: "span",
    mr: placement === 'right' ? margin : undefined,
    ml: placement === 'left' ? margin : undefined
  }, children), placement === 'right' && icon));
};

function _templateObject$o() {
  var data = _taggedTemplateLiteralLoose(["\n  ", ";\n  ", ";\n  ", ";\n  display: block;\n  max-width: 100%;\n  opacity: ", ";\n"]);

  _templateObject$o = function _templateObject() {
    return data;
  };

  return data;
}
var Image = /*#__PURE__*/styled.img.attrs(function (props) {
  if (props.rounded) {
    props.borderRadius = '50%';
  }

  return props;
})( /*#__PURE__*/_templateObject$o(), boxShadow, borderRadius, height, function (_ref) {
  var opacity = _ref.opacity;
  return opacity;
});
Image.defaultProps = {
  alt: '',
  opacity: 1,
  height: 'auto'
};

function _templateObject5$4() {
  var data = _taggedTemplateLiteralLoose(["\n    position: absolute;\n    z-index: 3;\n    transform: translate(-50%, 0);\n    left: calc(50% - #{width / 2});\n    bottom: ", "px;\n    color: inherit;\n    background-color: inherit;\n    padding: ", "px;\n    border-radius: ", ";\n    box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.12);\n    &:after {\n      content: '';\n      position: absolute;\n      width: 20px;\n      height: 20px;\n      bottom: -10px;\n      left: calc(50% - 10px);\n      background-color: inherit;\n      transform: rotate(45deg);\n      z-index: 4;\n    }\n  "]);

  _templateObject5$4 = function _templateObject5() {
    return data;
  };

  return data;
}

function _templateObject4$7() {
  var data = _taggedTemplateLiteralLoose(["\n  ", ";\n"]);

  _templateObject4$7 = function _templateObject4() {
    return data;
  };

  return data;
}

function _templateObject3$b() {
  var data = _taggedTemplateLiteralLoose(["\n    position: relative;\n    width: 1px;\n    height: 1px;\n    transform-origin: center bottom;\n    background-color: ", ";\n    border: 1px solid ", ";\n    animation: 600ms ", ";\n    /* Double pointer in css to let one handle the box-shadow and the other the mending with the body */\n    &:after {\n      content: '';\n      position: absolute;\n      width: 20px;\n      height: 20px;\n      bottom: ", "px;\n      left: calc(50% - 10px);\n      background-color: inherit;\n      transform: rotate(45deg);\n      z-index: 2;\n      box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.12);\n    }\n  "]);

  _templateObject3$b = function _templateObject3() {
    return data;
  };

  return data;
}

function _templateObject2$f() {
  var data = _taggedTemplateLiteralLoose(["\n  ", ";\n"]);

  _templateObject2$f = function _templateObject2() {
    return data;
  };

  return data;
}

function _templateObject$p() {
  var data = _taggedTemplateLiteralLoose(["\n  @keyframes fadeIn {\n    from {\n      opacity: 0;\n    }\n\n    to {\n      opacity: 1;\n    }\n  }\n"]);

  _templateObject$p = function _templateObject() {
    return data;
  };

  return data;
}
var fadeInAnimation = /*#__PURE__*/keyframes( /*#__PURE__*/_templateObject$p());
var Wrapper = /*#__PURE__*/styled(Box)( /*#__PURE__*/_templateObject2$f(), function (_ref) {
  var theme = _ref.theme,
      offset = _ref.offset;
  return css(_templateObject3$b(), theme.colors.white, theme.colors.borderGray, fadeInAnimation, offset);
});
var Body = /*#__PURE__*/styled(Box)( /*#__PURE__*/_templateObject4$7(), function (_ref2) {
  var theme = _ref2.theme,
      offset = _ref2.offset;
  return css(_templateObject5$4(), offset + 10, theme.space[3], function (_ref3) {
    var theme = _ref3.theme;
    return theme.radii['md'];
  });
});
var InfoWindow = function InfoWindow(_ref4) {
  var children = _ref4.children,
      _ref4$width = _ref4.width,
      width = _ref4$width === void 0 ? 200 : _ref4$width,
      _ref4$offset = _ref4.offset,
      offset = _ref4$offset === void 0 ? 5 : _ref4$offset,
      color = _ref4.color,
      props = _objectWithoutPropertiesLoose(_ref4, ["children", "width", "offset", "color"]);

  return React.createElement(Wrapper, Object.assign({
    offset: offset,
    color: color
  }, props), React.createElement(Body, {
    width: width,
    offset: offset
  }, children));
};

function _templateObject3$c() {
  var data = _taggedTemplateLiteralLoose(["\n      background: ", ";\n      border-color: transparent;\n      &:not(:last-child) {\n        border-right: 1px solid ", ";\n      }\n      &:not(:first-child) {\n        border-left: 1px solid ", ";\n      }\n    "]);

  _templateObject3$c = function _templateObject3() {
    return data;
  };

  return data;
}

function _templateObject2$g() {
  var data = _taggedTemplateLiteralLoose(["\n  border-radius: ", ";\n  background: ", ";\n  color: ", ";\n  user-select: none;\n  border: 1px solid ", ";\n\n  /* Addon to the left */\n  &:not(:last-child) {\n    border-top-right-radius: 0;\n    border-bottom-right-radius: 0;\n    border-right: none;\n  }\n\n  /* Addon to the right */\n  &:not(:first-child) {\n    border-top-left-radius: 0;\n    border-bottom-left-radius: 0;\n    border-left: none;\n  }\n\n  ", "\n"]);

  _templateObject2$g = function _templateObject2() {
    return data;
  };

  return data;
}

function _templateObject$q() {
  var data = _taggedTemplateLiteralLoose(["\n  position: relative;\n  width: 100%;\n\n  > input:not(:first-child) {\n    border-top-left-radius: 0;\n    border-bottom-left-radius: 0;\n  }\n\n  > input:not(:last-child) {\n    border-top-right-radius: 0;\n    border-bottom-right-radius: 0;\n  }\n"]);

  _templateObject$q = function _templateObject() {
    return data;
  };

  return data;
}
var InputGroup = /*#__PURE__*/styled(Flex)( /*#__PURE__*/_templateObject$q());
var InputGroupAddon = /*#__PURE__*/styled(Box)( /*#__PURE__*/_templateObject2$g(), function (props) {
  return props.theme.radii.md;
}, function (props) {
  return props.disabled ? props.theme.colors.neutral[1] : props.theme.colors.white;
}, function (props) {
  return props.disabled ? props.theme.colors.neutral[5] : props.theme.colors.text;
}, function (props) {
  return props.theme.colors.neutral[4];
}, function (props) {
  return props.alternative && css(_templateObject3$c(), props.disabled ? props.theme.colors.neutral[1] : props.theme.colors.formGray, props.theme.colors.neutral[4], props.theme.colors.neutral[4]);
});
InputGroupAddon.defaultProps = {
  py: inputDefaultProps.py,
  px: inputDefaultProps.px + 1
};

function _templateObject3$d() {
  var data = _taggedTemplateLiteralLoose(["\n  ", "\n"]);

  _templateObject3$d = function _templateObject3() {
    return data;
  };

  return data;
}

function _templateObject2$h() {
  var data = _taggedTemplateLiteralLoose(["\n  ", "\n"]);

  _templateObject2$h = function _templateObject2() {
    return data;
  };

  return data;
}

function _templateObject$r() {
  var data = _taggedTemplateLiteralLoose(["\n  box-sizing: border-box;\n  ", ";\n  ", ";\n  list-style: ", ";\n"]);

  _templateObject$r = function _templateObject() {
    return data;
  };

  return data;
}
var listCss = /*#__PURE__*/css( /*#__PURE__*/_templateObject$r(), /*#__PURE__*/rhythm(space$1), /*#__PURE__*/compose(layout, color, flexbox, typography, position, border), function (_ref) {
  var listStyle = _ref.listStyle;
  return listStyle;
});
var List = /*#__PURE__*/styled.ul( /*#__PURE__*/_templateObject2$h(), listCss);
List.defaultProps = {
  m: 0,
  p: 0,
  listStyle: 'none'
};
var Ol = /*#__PURE__*/styled.ol( /*#__PURE__*/_templateObject3$d(), listCss);
Ol.defaultProps = {
  m: 0,
  p: 0,
  listStyle: 'number'
};
var Li = function Li(_ref2) {
  var color = _ref2.color,
      props = _objectWithoutPropertiesLoose(_ref2, ["color"]);

  return React.createElement(Box, Object.assign({
    as: "li",
    color: color
  }, props));
};

function _templateObject2$i() {
  var data = _taggedTemplateLiteralLoose(["\n  animation: ", " 750ms linear infinite;\n"]);

  _templateObject2$i = function _templateObject2() {
    return data;
  };

  return data;
}

function _templateObject$s() {
  var data = _taggedTemplateLiteralLoose(["\n\tfrom {\n\t\ttransform: rotate(0deg);\n\t}\n\tto {\n\t\ttransform: rotate(360deg);\n\t}\n"]);

  _templateObject$s = function _templateObject() {
    return data;
  };

  return data;
}
var rotate360 = /*#__PURE__*/keyframes( /*#__PURE__*/_templateObject$s());
var Spinner = /*#__PURE__*/styled(Spinner$1)( /*#__PURE__*/_templateObject2$i(), rotate360);
Spinner.defaultProps = {
  size: 12
};

var LOADABLE_BUTTON_DEFAULT_SUCCESS_TIMEOUT_MS = 1000;
var LoadableButton = function LoadableButton(_ref) {
  var children = _ref.children,
      color = _ref.color,
      loading = _ref.loading,
      _ref$responseState = _ref.responseState,
      responseState = _ref$responseState === void 0 ? 'success' : _ref$responseState,
      _ref$responseTimeoutM = _ref.responseTimeoutMs,
      responseTimeoutMs = _ref$responseTimeoutM === void 0 ? LOADABLE_BUTTON_DEFAULT_SUCCESS_TIMEOUT_MS : _ref$responseTimeoutM,
      props = _objectWithoutPropertiesLoose(_ref, ["children", "color", "loading", "responseState", "responseTimeoutMs"]);

  var reducedMotion = useReducedMotion();
  var haveLoaded = React.useRef(false);

  var _useState = useState(false),
      success = _useState[0],
      setSuccess = _useState[1];

  useEffect(function () {
    if (loading) {
      haveLoaded.current = true;
    }
  }, [loading]);
  useEffect(function () {
    if (!responseTimeoutMs || !haveLoaded.current || loading) {
      return;
    }

    if (!loading) {
      setSuccess(true);
    }

    var timeoutId = setTimeout(function () {
      setSuccess(false);
      haveLoaded.current = false;
    }, responseTimeoutMs);
    return function () {
      clearTimeout(timeoutId);
    };
  }, [loading, responseTimeoutMs]);
  var a11yProps = {
    role: 'alert'
  };

  if (loading) {
    a11yProps['aria-busy'] = true;
    a11yProps['aria-label'] = 'loading';
  }

  return React.createElement(Button, Object.assign({
    color: color,
    pointerEvents: loading || success ? 'none' : 'auto',
    overflow: "hidden"
  }, props, a11yProps), React.createElement(Flex, {
    alignItems: "center",
    justifyContent: "center"
  }, children && React.createElement(motion.div, {
    initial: loading ? 'hidden' : 'visible',
    variants: {
      visible: {
        y: 0,
        opacity: 1,
        transition: {
          duration: 0.2
        },
        visibility: 'visible'
      },
      hidden: {
        y: reducedMotion ? 0 : -50,
        opacity: 0,
        transition: {
          duration: 0.2
        },
        transitionEnd: {
          visibility: 'hidden'
        }
      }
    },
    animate: !loading && !success ? 'visible' : 'hidden'
  }, children), React.createElement(AnimatePresence, {
    initial: false,
    exitBeforeEnter: true
  }, loading && React.createElement(motion.div, {
    key: "loading",
    style: {
      position: 'absolute',
      display: 'flex',
      outline: 'none'
    },
    initial: {
      y: reducedMotion ? 0 : -50
    },
    animate: {
      y: 0,
      transition: {
        duration: 0.4
      }
    },
    exit: {
      scale: reducedMotion ? 1 : 0,
      transition: {
        duration: 0.1
      }
    }
  }, React.createElement(Spinner, null)), success && !loading && React.createElement(motion.div, {
    key: "success",
    style: {
      position: 'absolute',
      display: 'flex',
      outline: 'none'
    },
    initial: {
      scale: reducedMotion ? 1 : 0,
      opacity: reducedMotion ? 0 : 1
    },
    animate: {
      scale: reducedMotion ? 1 : [null, 2, 1, 1],
      opacity: 1,
      transition: {
        duration: 0.4
      }
    },
    exit: {
      scale: reducedMotion ? 1 : 0,
      opacity: reducedMotion ? 0 : 1,
      transition: {
        duration: 0.2
      }
    }
  }, responseState === 'success' && React.createElement(Check, {
    size: 14
  }), responseState === 'error' && React.createElement(Warning, {
    size: 18
  })))));
};

var PropertiesBox = function PropertiesBox(_ref) {
  var title = _ref.title,
      rows = _ref.rows,
      _ref$headerBackground = _ref.headerBackground,
      headerBackground = _ref$headerBackground === void 0 ? 'primary' : _ref$headerBackground,
      _ref$headerColor = _ref.headerColor,
      headerColor = _ref$headerColor === void 0 ? 'white' : _ref$headerColor,
      border = _ref.border;
  return React.createElement(Flex, {
    height: "100%",
    borderRadius: "lg",
    column: true,
    border: border ? '1px solid #D2D4DD' : '',
    pb: "xs"
  }, React.createElement(Box, {
    bg: headerBackground,
    borderTopLeftRadius: "lg",
    borderTopRightRadius: "lg",
    p: "xxs"
  }, React.createElement(Text, {
    color: headerColor
  }, title)), rows.map(function (row, i) {
    return React.createElement(Grid, {
      key: i
    }, React.createElement(GridCell, {
      width: {
        xs: 1,
        md: 1 / 2
      }
    }, React.createElement(Text, {
      pt: "xs",
      pl: "xs",
      pr: "xs"
    }, row.property)), React.createElement(GridCell, {
      width: {
        xs: 1,
        md: 1 / 2
      }
    }, React.createElement(Text, {
      pt: "xs",
      pl: "xs",
      pr: "xs"
    }, row.value)));
  }));
};

function _templateObject5$5() {
  var data = _taggedTemplateLiteralLoose(["\n  transform: translateY(-50%);\n"]);

  _templateObject5$5 = function _templateObject5() {
    return data;
  };

  return data;
}

function _templateObject4$8() {
  var data = _taggedTemplateLiteralLoose(["\n      color: ", ";\n      background-color: ", ";\n    "]);

  _templateObject4$8 = function _templateObject4() {
    return data;
  };

  return data;
}

function _templateObject3$e() {
  var data = _taggedTemplateLiteralLoose(["\n      background-color: ", ";\n      border-color: transparent;\n    "]);

  _templateObject3$e = function _templateObject3() {
    return data;
  };

  return data;
}

function _templateObject2$j() {
  var data = _taggedTemplateLiteralLoose(["\n  ", ";\n  appearance: none;\n  transition: border-color 150ms ease-out;\n  &:focus {\n    outline: none;\n    border-color: ", ";\n  }\n  ", "\n  ", "\n"]);

  _templateObject2$j = function _templateObject2() {
    return data;
  };

  return data;
}

function _templateObject$t() {
  var data = _taggedTemplateLiteralLoose(["\n  ", ";\n  border: none;\n"]);

  _templateObject$t = function _templateObject() {
    return data;
  };

  return data;
}
var ICON_SIZE = 18;
var SelectWrapper = /*#__PURE__*/styled(Box)( /*#__PURE__*/_templateObject$t(), base);
SelectWrapper.defaultProps = /*#__PURE__*/_extends({}, inputDefaultProps, {
  px: undefined,
  py: undefined
});
var SelectBase = /*#__PURE__*/styled.select( /*#__PURE__*/_templateObject2$j(), /*#__PURE__*/compose(color, space$1, height, width, fontSize, border), function (props) {
  return props.theme.colors.primary;
}, function (props) {
  return props.alternative && css(_templateObject3$e(), props.disabled ? props.theme.colors.neutral[1] : props.theme.colors.formGray);
}, function (props) {
  return props.disabled && css(_templateObject4$8(), props.theme.colors.neutral[5], props.theme.colors.neutral[1]);
});
SelectBase.displayName = 'SelectBase';
SelectBase.defaultProps = {
  width: 1,
  color: 'text',
  backgroundColor: 'white',
  border: '1px solid',
  borderColor: 'neutral.3',
  borderRadius: '6px',
  py: inputDefaultProps.py,
  pl: inputDefaultProps.px,
  pr: ICON_SIZE * 3,
  fontSize: inputDefaultProps.fontSize
};
var IconWrapper = /*#__PURE__*/styled(Box)( /*#__PURE__*/_templateObject5$5());
var Select = function Select(_ref) {
  var color = _ref.color,
      props = _objectWithoutPropertiesLoose(_ref, ["color"]);

  return React.createElement(SelectWrapper, {
    position: "relative"
  }, React.createElement(SelectBase, Object.assign({
    color: color
  }, props)), React.createElement(IconWrapper, {
    position: "absolute",
    right: ICON_SIZE,
    height: ICON_SIZE,
    top: "50%"
  }, React.createElement(Down, {
    color: "primary",
    size: ICON_SIZE
  })));
};

// presets are used to override theme defaults
var adminFontSizes = [12, 14, 16, 24, 30, 40, 60, 70];
var admin = {
  preset: 'admin',
  fontSizes: adminFontSizes,
  textSize: adminFontSizes[2],
  maxWidth: '1600px'
};
var stationClientFontSizes = [14, 16, 24, 36, 50, 60, 75, 90];
var stationClient = {
  preset: 'stationClient',
  fontSizes: stationClientFontSizes,
  textSize: stationClientFontSizes[2]
};
var presets = {
  admin: admin,
  stationClient: stationClient
};

function _templateObject11() {
  var data = _taggedTemplateLiteralLoose(["\n  white-space: ", ";\n"]);

  _templateObject11 = function _templateObject11() {
    return data;
  };

  return data;
}

function _templateObject10() {
  var data = _taggedTemplateLiteralLoose(["\n  ", "\n"]);

  _templateObject10 = function _templateObject10() {
    return data;
  };

  return data;
}

function _templateObject9() {
  var data = _taggedTemplateLiteralLoose(["\n      thead th {\n        background: none;\n        border: none;\n      }\n    "]);

  _templateObject9 = function _templateObject9() {
    return data;
  };

  return data;
}

function _templateObject8$1() {
  var data = _taggedTemplateLiteralLoose(["\n      td,\n      th {\n        font-size: ", "px !important;\n        padding: ", "px !important;\n      }\n    "]);

  _templateObject8$1 = function _templateObject8() {
    return data;
  };

  return data;
}

function _templateObject7$2() {
  var data = _taggedTemplateLiteralLoose(["\n      thead + tbody tr:first-child {\n        ", ", ", " {\n          border-top-width: 0;\n          ", "\n        }\n      }\n    "]);

  _templateObject7$2 = function _templateObject7() {
    return data;
  };

  return data;
}

function _templateObject6$2() {
  var data = _taggedTemplateLiteralLoose(["\n  ", "\n  width: 100%;\n  border-collapse: separate;\n  border-spacing: 0;\n  tbody tr {\n    background: white;\n  }\n  tr:first-child {\n    ", ", ", " {\n      border-top-width: 1px;\n      ", "\n    }\n  }\n  tr:last-child {\n    ", ", ", " {\n      ", "\n    }\n  }\n  ", "\n  thead tr:first-child {\n    ", ", ", " {\n      ", "\n    }\n  }\n  tfoot {\n    ", ", ", " {\n      border-width: 0;\n      font-size: ", "px;\n    }\n  }\n  tfoot tr:first-child {\n    ", ", ", " {\n      border-top-width: 0;\n    }\n  }\n  ", "\n  ", "\n"]);

  _templateObject6$2 = function _templateObject6() {
    return data;
  };

  return data;
}

function _templateObject5$6() {
  var data = _taggedTemplateLiteralLoose(["\n    &:first-child {\n      border-bottom-left-radius: ", ";\n    }\n    &:last-child {\n      border-bottom-right-radius: ", ";\n    }\n  "]);

  _templateObject5$6 = function _templateObject5() {
    return data;
  };

  return data;
}

function _templateObject4$9() {
  var data = _taggedTemplateLiteralLoose(["\n    &:first-child {\n      border-top-left-radius: ", ";\n    }\n    &:last-child {\n      border-top-right-radius: ", ";\n    }\n  "]);

  _templateObject4$9 = function _templateObject4() {
    return data;
  };

  return data;
}

function _templateObject3$f() {
  var data = _taggedTemplateLiteralLoose(["\n  ", "\n  thead & {\n    background-color: ", ";\n  }\n"]);

  _templateObject3$f = function _templateObject3() {
    return data;
  };

  return data;
}

function _templateObject2$k() {
  var data = _taggedTemplateLiteralLoose(["\n  ", "\n"]);

  _templateObject2$k = function _templateObject2() {
    return data;
  };

  return data;
}

function _templateObject$u() {
  var data = _taggedTemplateLiteralLoose(["\n  ", ";\n  &:first-child {\n    border-left-width: 1px;\n  }\n  &:last-child {\n    border-right-width: 1px;\n  }\n"]);

  _templateObject$u = function _templateObject() {
    return data;
  };

  return data;
}
var defaultRadius = 'md';
var cellCss = /*#__PURE__*/css( /*#__PURE__*/_templateObject$u(), /*#__PURE__*/compose(border, color, typography, space$1, width, verticalAlign));
var Td = /*#__PURE__*/styled.td( /*#__PURE__*/_templateObject2$k(), cellCss);
Td.defaultProps = {
  textAlign: 'left',
  py: rhythms.xxs,
  px: rhythms.xs,
  borderColor: 'borderGray',
  borderWidth: '0 0 1px',
  borderStyle: 'solid',
  fontSize: 1,
  verticalAlign: 'top'
};
var Th = /*#__PURE__*/styled.th( /*#__PURE__*/_templateObject3$f(), cellCss, function (_ref) {
  var theme = _ref.theme;
  return theme.colors.neutral[1];
});
Th.defaultProps = {
  textAlign: 'left',
  py: rhythms.xxs,
  px: rhythms.xs,
  borderColor: 'borderGray',
  borderWidth: '0 0 1px',
  borderStyle: 'solid',
  fontSize: 0,
  fontWeight: 'normal',
  verticalAlign: 'top'
};

var borderRadiusTop = function borderRadiusTop(radius) {
  return function (_ref2) {
    var theme = _ref2.theme;
    return css(_templateObject4$9(), theme.radii[radius], theme.radii[radius]);
  };
};

var borderRadiusBottom = function borderRadiusBottom(radius) {
  return function (_ref3) {
    var theme = _ref3.theme;
    return css(_templateObject5$6(), theme.radii[radius], theme.radii[radius]);
  };
};

var Table = /*#__PURE__*/styled.table( /*#__PURE__*/_templateObject6$2(), space$1, Td, Th, /*#__PURE__*/borderRadiusTop(defaultRadius), Td, Th, /*#__PURE__*/borderRadiusBottom(defaultRadius), function (props) {
  return props.variant !== 'simple' && css(_templateObject7$2(), Td, Th, borderRadiusTop('none'));
}, Td, Th, /*#__PURE__*/borderRadiusBottom('none'), Td, Th, function (_ref4) {
  var theme = _ref4.theme;
  return theme.fontSizes[0];
}, Td, Th, function (props) {
  if (!props.small) {
    return;
  }

  return css(_templateObject8$1(), props.theme.fontSizes[0], props.theme.space[2]);
}, function (props) {
  if (!props.variant || props.variant !== 'simple') {
    return;
  }

  return css(_templateObject9());
});
var Caption = /*#__PURE__*/styled.caption( /*#__PURE__*/_templateObject10(), /*#__PURE__*/compose(typography, space$1));
Caption.defaultProps = {
  color: 'neutral.5',
  fontSize: 1,
  mb: 2,
  textAlign: 'left'
};
var Tr = /*#__PURE__*/styled.tr( /*#__PURE__*/_templateObject11(), function (props) {
  return props.whiteSpace;
});

function _templateObject$v() {
  var data = _taggedTemplateLiteralLoose(["\n  ", "\n"]);

  _templateObject$v = function _templateObject() {
    return data;
  };

  return data;
}
var Textarea = /*#__PURE__*/styled.textarea( /*#__PURE__*/_templateObject$v(), inputCss);
Textarea.defaultProps = inputDefaultProps;

export { Alert, Anchor, AnchorButton, Arrow, BaseStyles, Box, Button, ButtonAnchor, ButtonGroup, COOL_PADDING_Y, COOL_PADDING_Y_SMALL, Caption, Checkbox, CheckboxStateless, Container, CssGrid, Dialog, DialogBackdrop, DialogStateless, DialogWrapper, Dot, Dropdown, DropdownCaption, DropdownInput, DropdownItem, DropdownItemLink, Flex, FormField, Grid, GridCell, H1, H2, H3, Heading, Hide, IconButton, Image, InfoWindow, Input, InputGroup, InputGroupAddon, LOADABLE_BUTTON_DEFAULT_SUCCESS_TIMEOUT_MS, Label$1 as Label, Li, List, LoadableButton, Ol, PropertiesBox, ScrollLock, Select, Spinner, Table, Td, Text, Textarea, Th, Tr, emToPx, getTheme, getTypoStyles, inputCss, inputDefaultProps, mutedAttrs, presets, rhythm, rhythms, theme, typoStyles, useStyledComponentsTheme, warnOnce };
//# sourceMappingURL=react-ui-kit.esm.js.map
